import Calendar from '../store/schema/Calendar';
import CalendarEntryExtended from '../store/schema/CalendarEntryExtended';
import CalendarGroupExtended from '../store/schema/CalendarGroupExtended';
import CalendarId from '../store/schema/CalendarId';
import getStore from '../store/store';
import getCalculatedFolderId from '../utils/getCalculatedFolderId';
import { getUserMailboxInfo } from 'owa-client-ids';
import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import LinkedCalendarEntry from 'owa-service/lib/contract/LinkedCalendarEntry';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';

export function getDefaultCalendar(primarySmtpAddress?: string): Calendar {
    if (!primarySmtpAddress) {
        // get default account's primarySmtpAddress
        primarySmtpAddress = getUserMailboxInfo().primarySmtpAddress;
    }
    return getStore().defaultCalendars.get(primarySmtpAddress);
}

export function getCalendarEntries(): CalendarEntryExtended[] {
    const { calendarEntryMapping } = getStore();
    return [...calendarEntryMapping.values()];
}

export function getAllCalendars(): Calendar[] {
    return (
        getCalendarEntries()
            .map(calendarEntry => getCalendarByCalendarId(calendarEntry.id.id))
            // Filter out null calendars
            .filter(x => x)
    );
}

export function getCalendarGroups(): CalendarGroupExtended[] {
    const { calendarGroupsMapping } = getStore();
    return [...calendarGroupsMapping.values()];
}

export function getCalendarIdByFolderId(folderId: string): CalendarId {
    const { folderIdToCalendarId } = getStore();
    return folderIdToCalendarId.get(folderId);
}

export function getCalendarEntryByCalendarId(calendarId: string) {
    const { calendarEntryMapping } = getStore();
    return calendarEntryMapping.get(calendarId);
}

export function getCalendarByCalendarId(calendarId: string): Calendar {
    const calendarEntry = getCalendarEntryByCalendarId(calendarId);
    if (!calendarEntry) {
        // If we ask for a calendarId that is not in the cache, then return null
        return null;
    }

    return {
        id: {
            id: calendarId,
            mailboxInfo: { ...calendarEntry.id.mailboxInfo },
        } as CalendarId,
        calculatedFolderId: getCalculatedFolderIdByCalendarID(calendarId),
        displayName: calendarEntry.CalendarName,
        colorScheme: null,
    } as Calendar;
}

export function getCalendarFolder(calendar: Calendar): CalendarFolder {
    const { calendarFolderMapping } = getStore();
    return calendarFolderMapping.get(calendar.id.id);
}

export function getCalendarEntry(calendar: Calendar) {
    return getCalendarEntryByCalendarId(calendar.id.id);
}

export function getCalendarEntryByFolderId(folderId: string) {
    const calendarId = getCalendarIdByFolderId(folderId);

    // If the calendar ID is present then get the CalendarEntry for it otherwise return undefined
    return calendarId ? getCalendarEntryByCalendarId(calendarId.id) : undefined;
}

/**
 * Returns the CalendarFolder for a given FolderId
 * @param folderId FolderId of the calendar folder
 */
export function getCalendarFolderByFolderId(folderId: string): CalendarFolder | undefined {
    const { calendarFolderMapping } = getStore();
    const calendarId = getCalendarIdByFolderId(folderId);

    // If the calendar ID is present then get the CalendarFolder for it otherwise return undefined
    return calendarId ? calendarFolderMapping.get(calendarId.id) : undefined;
}

export function getChangeKeyByCalendarItemId(calendarItemId: string) {
    const { calendarItemIdToChangeKey } = getStore();
    return calendarItemIdToChangeKey.get(calendarItemId);
}

export function getCalendarFolderId(calendar: Calendar): string {
    return getFolderIdByCalendarID(calendar.id.id);
}

export function getFolderIdByCalendarID(calendarId: string): string {
    const { calendarFolderMapping } = getStore();
    const calendarFolder = calendarFolderMapping.get(calendarId);
    if (calendarFolder && (calendarFolder.FolderId || calendarFolder.DistinguishedFolderId)) {
        return calendarFolder.FolderId
            ? calendarFolder.FolderId.Id
            : calendarFolder.DistinguishedFolderId;
    } else {
        return getCalculatedFolderIdByCalendarID(calendarId);
    }
}

export function getCalculatedFolderIdByCalendarID(calendarId: string): string {
    const { calendarEntryMapping } = getStore();

    const calendarEntry = calendarEntryMapping.get(calendarId);
    if (calendarEntry) {
        const folderId = getCalculatedFolderId(calendarEntry);
        return folderId ? folderId.Id : null;
    }
    return null;
}

export function calendarIsInCache(calendarId: string): boolean {
    return getCalendarEntryByCalendarId(calendarId) ? true : false;
}

/**
 * Returns whether this folder is a LocalCalendarEntry or a LocalCacheForRemoteCalendarEntry
 * that has a valid CalendarFolderId
 * @param folderId FolderId of the calendar folder
 */
export function isLocalCalendarEntry(folderId: string): boolean {
    const { folderIdToCalendarId, calendarEntryMapping } = getStore();

    const calendarId = folderIdToCalendarId.get(folderId);
    if (calendarId) {
        var calendarEntry: any = calendarEntryMapping.get(calendarId.id);

        // Group mailboxes are Linked calendar entries
        return !calendarEntry.IsGroupMailboxCalendar && calendarEntry.CalendarFolderId != undefined;
    } else {
        return false;
    }
}

/**
 * Returns whether the given folder is a readonly local calendar entry
 * @param folderId The folder id
 * @returns True if it's a read only local calendar entry, false otherwise
 */
export function isReadOnlyLocalCalendar(folderId: string): boolean {
    if (isLocalCalendarEntry(folderId)) {
        let calendarEntry: LocalCalendarEntry = getCalendarEntryByFolderId(folderId);
        return calendarEntry && calendarEntry.IsReadOnly;
    }
    return false;
}

/**
 * Checks whether the calendarEntry has been marked as an invalid folder
 * @param calendarEntry the calendar folder to check for validity
 */
export function isValidCalendarEntry(calendarEntry: CalendarEntry) {
    const { validEntryMapping } = getStore();

    return calendarEntry && validEntryMapping.get(calendarEntry.ItemId.Id);
}

/**
 * Checks if the calendar is a LocalCacheForRemoteCalendarEntry calendar with an
 * invalid folder id. When the cache is initialized LocalCacheForRemoteCalendarEntry
 * calendar entries do not have accurate folder ids. They should be fetched/ populated as needed.
 */
export function calendarFolderIdNeedsUpdate(calendarId: string): boolean {
    const folderId = getFolderIdByCalendarID(calendarId);
    if (!isLocalCalendarEntry(folderId)) {
        const calendarEntry: LocalCacheForRemoteCalendarEntry = getCalendarEntryByCalendarId(
            calendarId
        ) as LocalCacheForRemoteCalendarEntry;
        return !isValidCalendarEntry(calendarEntry);
    }
    return false;
}

export function getCalendarEntryByEmailAddress(emailAddress: string): CalendarEntry | undefined {
    const { folderIdToCalendarId, calendarEntryMapping } = getStore();
    const calendarId = folderIdToCalendarId.get(emailAddress);
    let calendarEntry;

    // If the calendar ID is present then get the CalendarEntry for it,
    // otherwise search through calendar entries for a LocalCacheForRemoteCalendarEntry
    if (calendarId) {
        calendarEntry = calendarEntryMapping.get(calendarId.id);
    } else {
        let entries = [...calendarEntryMapping.values()] as (
            | LinkedCalendarEntry
            | LocalCacheForRemoteCalendarEntry)[];

        calendarEntry = entries.filter(entry => {
            return (
                entry.OwnerEmailAddress &&
                entry.OwnerEmailAddress.toLowerCase() == emailAddress.toLowerCase()
            );
        })[0];
    }

    return calendarEntry;
}
